<template>
  <div
    class="table-responsive"
    :style="overflowStyle"
  >
    <!--
      @slot Slot to show the Toolbar
    -->
    <slot name="sb-toolbar" />
    <!--
      @slot Slot to show the Tabs
    -->
    <slot name="sb-tabs" />
    <!--
      @slot Slot to show the Filters
    -->
    <slot name="sb-filters" />
    <SbLoadingPromiseWrapper
      v-model:loading="loading"
      size="xtsm"
      :loading-promise="loadingPromise"
      :classes="classes"
    >
      <table
        class="table"
        role="presentation"
        :class="[
          variation == 'primary' ? 'table-bordered' : '',
          variation
        ]"
      >
        <template v-if="$slots['sb-table-header']">
          <!--
            @slot Slot to show the Table Header
          -->
          <slot
            name="sb-table-header"
            :cols="columnDefinitions"
          />
        </template>
        <SbTableThead v-else />
        <tbody v-if="loading && $slots['sb-table-loading-state']">
          <tr>
            <td :colspan="columnDefinitions.length">
              <!--
                @slot Slot to show the Loading State
              -->
              <slot name="sb-table-loading-state" />
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="loading && !$slots['sb-table-loading-state']">
          <tr>
            <td :colspan="columnDefinitions.length">
              <span class="no-rows">
                <sb-text value="common.fetchingData" />...
              </span>
            </td>
          </tr>
        </tbody>
        <tbody
          v-if="!$slots['sb-table-row'] && rows.length"
          class="sb-table-content"
        >
          <SbTableRowPasser
            v-for="(row, index) in rows"
            :key="resolveRowKey(row)"
            :index="index"
            :row="row"
          >
            <SbTableRow>
              <SbTableCell
                v-for="col in columnDefinitions"
                :key="col.field"
                :width="col.width"
              >
                <span v-if="col.isSFC">
                  <component
                    :is="col?.cell"
                    :value="row[col.field]"
                  />
                </span>
                <span
                  v-else
                  :title="row[col.field]"
                >
                  {{ row[col.field] }}
                </span>
              </SbTableCell>
            </SbTableRow>
          </SbTableRowPasser>
        </tbody>
        <tbody
          v-else-if="rows.length"
          class="sb-table-content"
        >
          <SbTableRowPasser
            v-for="(row, index) in rows"
            :key="resolveRowKey(row)"
            :index="index"
            :row="row"
          >
            <!--
              @slot Slot to show the Individual Table Row
            -->
            <slot
              name="sb-table-row"
              :row="row"
              :index="index"
              :column-widths="columnWidths"
            />
          </SbTableRowPasser>
          <slot />
        </tbody>
        <tbody v-else-if="!loading && $slots['sb-table-empty-state']">
          <tr>
            <td :colspan="emptyTableColSpan">
              <!--
                  @slot Slot to show the Table Empty State
                -->
              <slot name="sb-table-empty-state" />
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="!loading">
          <tr>
            <td :colspan="emptyTableColSpan">
              <span class="no-rows">
                <sb-text value="sbTable.noRows" />...
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </SbLoadingPromiseWrapper>
    <!--
      @slot Slot to show the Pagination Controls
    -->
    <slot name="pagination" />
  </div>
</template>

<script>
import _isEqual from 'lodash/isEqual';
import _intersectionWith from 'lodash/intersectionWith';

import SbLoadingPromiseWrapper from '@src/components/Loading/SbLoadingPromiseWrapper.vue';
import SbText from '@src/components/Text/SbText.vue';
import uuid from '@src/utils/uuid';
import SbColumnModel from '@src/components/Table/utils/sb-table-model';
import SbTableThead from '@src/components/Table/components/SbTableThead.vue';
import SbTableRow from '@src/components/Table/components/SbTableRow.vue';
import SbTableCell from '@src/components/Table/components/SbTableCell.vue';
import SbTableRowPasser from '@src/components/Table/components/SbTableRowPasser.vue';

import * as SelectableMixin from '@src/components/Table/utils/selectableMixin';

/**
 * A UI Grid that can be used to display tabular information.
 */
export default {
  name: 'SbTable',
  components: {
    SbTableThead,
    SbTableRow,
    SbTableCell,
    SbTableRowPasser,
    SbLoadingPromiseWrapper,
    SbText,
  },
  mixins: [SelectableMixin.default],
  provide() {
    this.sbTable.definitions = this.columnDefinitions;
    const { sbTable } = this;
    const { sbTableSort } = this;
    return {
      sbTable,
      sbTableSort,
      sortTable: this.sortTable,
      sbSelectableTable: this.sbSelectableTable,
    };
  },
  props: {
    /**
    * The column definitions required for the table
    */
    columnDefinitions: {
      required: true,
      type: Array,
      validator(value) {
        if (!value.length) {
          console.warn('please supply atleast one column definition') // eslint-disable-line
          return false;
        }
        // validate shape
        const expectedShape = Object.keys(SbColumnModel);
        if (!value.every((v) => Object.keys(v).every((k) => expectedShape.indexOf(k) !== -1))) {
          const expectedMsg = expectedShape.reduce((acc, k) => {
            // eslint-disable-next-line no-param-reassign
            acc += `${k},`;
            return acc;
          }, '');
          console.warn(`Expected all items in the columnDefinitions prop to have the following properties: ${expectedMsg.substring(0, expectedMsg.length - 1)}`) // eslint-disable-line
          return false;
        }
        return true;
      },
    },
    /**
     * Style variation to give additional meaning.
     * `primary,  minimal
     */
    variation: {
      type: String,
      default: 'primary',
      validator: (value) => value.match(/(primary|minimal|)/),
    },
    /**
     * The rows corresponding to the rows of the table
     */
    rows: {
      type: Array,
      default: () => ([]),
    },
    /**
     * The promise to show loading indicator, removes indicator when promise is resolved or rejected
     */
    loadingPromise: {
      type: Promise,
      default: null,
    },
    /**
     * Default sort column
     */
    defaultSortColumn: {
      type: Object,
      default: null,
    },
    /**
     * Default sorting order
     */
    defaultSortOrdering: {
      type: String,
      default: 'desc',
      validator: (value) => value.match(/(desc|asc)/),
    },
    /**
     * Used to set the type of selectio strategy
     */
    selectable: SelectableMixin.selectableProp(),
    // List of selected rows that will be sync with the sbSelectableTable.selectedRows
    /**
     * The rows that are currently selected
     */
    selectedRows: {
      type: Array,
      default: () => ([]),
    },
    /**
     * The unique ID identifying each of the rows
     */
    rowKey: {
      type: String,
      default: '',
    },
  },
  emits: [
    /**
     * Emitted when the selected value is changed in the table with the new selected value
     */
    'onSelect',
    /**
     * Emitted with sorting info for a sorted column
     */
    'sort',
    /**
     * Emitted when it is a selectable nested table and the selection changes.
    */
    'row-selection-updated-selectable-sub-table',
  ],
  data() {
    return {
      componentName: 'SbTable',
      newComponent: 'SbTableExpandableRows',
      sbSelectableTable: {
        type: null,
        selectedRows: [],
        selectableRows: [],
        selectedRow: null,
      },
      sbTable: {
        definitions: {},
      },
      sbTableSort: {
        sortColumn: null,
        descending: true,
      },
      loading: false,
      rowKeys: new WeakMap(),
    };
  },
  computed: {
    overflowStyle() {
      return this.loading ? 'overflow:hidden;' : 'overflow:auto';
    },
    classes() {
      return this.loading ? ['sb-table-wrapper'] : ['sb-table-wrapper', 'sb-table-custom-scroll'];
    },
    emptyTableColSpan() {
      const span = this.columnDefinitions.length;
      return this.isSelectable ? span + 1 : span;
    },
    columnWidths() {
      return this.columnDefinitions.reduce((acc, cd) => {
        acc[cd.field] = cd.width;
        return acc;
      }, {});
    },
  },
  watch: {
    columnDefinitions(n) {
      if (n.length > 0) {
        this.sbTable.definitions = n;
      }
    },
    selectable: {
      handler(n) {
        this.sbSelectableTable.type = n;
      },
      immediate: true,
    },
    'sbSelectableTable.selectedRows': function (newSelectedRows, oldSelectedRows) {
      if (!_isEqual(newSelectedRows, oldSelectedRows)) {
        this.$emit('onSelect', newSelectedRows);
      }
    },
    'sbSelectableTable.selectedRow': function (n) {
      this.$emit('onSelect', n);
    },
    selectedRows: {
      handler(newSelectedRows, oldSelectedRows) {
        if (_isEqual(newSelectedRows, oldSelectedRows)) {
          return;
        }
        this.sbSelectableTable.selectedRows = newSelectedRows;
      },
      immediate: true,
    },
    rows(n) {
      this.resetSelection(n);
    },
  },
  mounted() {
    this.sbTableSort.sortColumn = this.defaultSortColumn;
    this.sbTableSort.descending = this.defaultSortOrdering === 'desc';
  },
  methods: {
    resetSelection(rows) {
      this.sbSelectableTable.selectedRows = _intersectionWith(rows, this.sbSelectableTable.selectedRows, _isEqual);
      this.sbSelectableTable.selectableRows = rows;
    },
    sortTable(columnDefinition, descending) {
      this.sbTableSort.sortColumn = columnDefinition;
      this.sbTableSort.descending = descending;
      const sortData = {
        sortColumn: this.sbTableSort.sortColumn,
        descending: this.sbTableSort.descending,
      };
      this.$emit('sort', sortData);
    },
    resolveRowKey(row) {
      let key = row[this.rowKey];
      if (key) return key;

      key = this.rowKeys.get(row);

      if (!key) {
        key = `sb-table-row-${uuid()}`;
        this.rowKeys.set(row, key);
      }
      return key;
    },
  },
};
</script>

<style lang="scss">
@import '@src/components/Table/style/sb-table.scss';
@import '@src/components/Table/style/sb-table-variations.scss';
</style>
